import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomUser, DataRoom, UnlockUser, AssociatedTeamMember } from '../../../shared/models';
import { EntityUtils } from '../../../shared/utils';

interface TeamMemberActionButtonProps {
	isEmployee: boolean;
	isBorrower: boolean;
	isBanker: boolean;
	user: DataRoomUser;
	dataRoom: DataRoom;
	member: AssociatedTeamMember;
	unlockUser: (user: UnlockUser) => void;
	accessLogs: (user: UnlockUser) => void;
	removeTeamMember: (member: AssociatedTeamMember) => void;
	sendInvite: (member: UnlockUser) => void;
	contactDetails: (member: AssociatedTeamMember) => void;
}

type TeamMemberActionItem = keyof typeof TeamMemberActionItemMap;
const TeamMemberActionItemMap = {
	Unlock: 'Unlock Team Member',
	Delete: 'Delete',
	AccessLogs: 'View Access Logs',
	SendInvite: 'Send Invite',
	ContactDetails: 'Contact Details',
};

export const TeamMemberActionButton: React.FC<TeamMemberActionButtonProps> = ({
	isEmployee,
	isBorrower,
	isBanker,
	user,
	dataRoom,
	member,
	unlockUser,
	accessLogs,
	removeTeamMember,
	sendInvite,
	contactDetails,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isRoomOpen = dataRoom.roomStatus == 'Open';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: TeamMemberActionItem) => {
		const userDto = EntityUtils.GetUnlockUserFromAssociatedTeamMember(member, user);

		switch (action) {
			case 'Unlock':
				unlockUser(userDto ?? ({} as UnlockUser));
				break;
			case 'SendInvite':
				sendInvite(userDto ?? ({} as UnlockUser));
				break;
			case 'Delete':
				removeTeamMember(member);
				break;
			case 'AccessLogs':
				accessLogs(userDto ?? ({} as UnlockUser));
				break;
			case 'ContactDetails':
				contactDetails(member);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: TeamMemberActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ContactDetails')}>
											{TeamMemberActionItemMap['ContactDetails']}
										</MenuItem>
									)}
									{isEmployee && isRoomOpen && !member.isLockedOut && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SendInvite')}>
											{TeamMemberActionItemMap['SendInvite']}
										</MenuItem>
									)}
									{!isBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AccessLogs')}>
											{TeamMemberActionItemMap['AccessLogs']}
										</MenuItem>
									)}
									{isEmployee && user.isLockedOut && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Unlock')}>
											{TeamMemberActionItemMap['Unlock']}
										</MenuItem>
									)}
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
											{TeamMemberActionItemMap['Delete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
